import React from 'react';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import { IConversation } from '@bb/domain/dist/types';
import { Text } from '../../Typography';
import { Avatar } from '../../Avatar';

const AVATAR_WIDTH = 50;

export type ConversationType = IConversation & {
  id: string;
  isUnread?: boolean;
  otherUsers?: MessagingMe[];
  users?: MessagingMe[];
  remove?: boolean;
};

export type MessagingMe = {
  id: string;
  firstName: string;
  lastName: string;
  role?: string;
  avatarFilestackId?: string;
  unreadConvos?: (IConversation & { id: string })[];
};

const StyledAvatarWrapper = styled.div<{ $front: boolean }>`
  && {
    > span {
      box-shadow: 0px 0px 0px 2px rgba(255, 255, 255, 1);
    }
    margin-right: 10px;
    ${({ $front }) =>
      $front &&
      ` position: absolute;
        top: 10px;
        left: 10px;
        margin: 0;
      `}
  }
`;

const StyledGridCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`;

const getOtherName = (convo: ConversationType) => {
  const { otherUsers } = convo;
  let result = otherUsers
    .slice(0, 2)
    .map(user => `${user.firstName} ${user.lastName}`)
    .join(', ');

  if (otherUsers.length > 2) {
    result += `... +${otherUsers.length - 2}`;
  }

  return result;
};

const getUsername = (user: MessagingMe, me: MessagingMe) => {
  const { id: myId } = me;
  if (myId === user.id) {
    return 'Me';
  }
  return user.firstName;
};

const getLastSenderUsername = (convo: ConversationType, me: MessagingMe) => {
  const lastSenderUser = convo.userInfo.find(
    user => user.id === convo.latestSender.toString(),
  );

  if (!lastSenderUser) {
    return 'Frontdesk';
  }

  return getUsername(lastSenderUser, me);
};

const onConvoItemClick = (convoId: string) => {
  axios.get(`/messaging/openConvo/${convoId}`);
};

const getAvatar = (convo: ConversationType) => {
  const { otherUsers } = convo;
  if (otherUsers.length < 2) {
    return (
      <StyledAvatarWrapper $front={false}>
        <Avatar user={otherUsers[0]} width={AVATAR_WIDTH} />
      </StyledAvatarWrapper>
    );
  }

  return (
    <>
      <StyledAvatarWrapper $front={false}>
        <Avatar user={otherUsers[1]} width={AVATAR_WIDTH - 2} />
      </StyledAvatarWrapper>
      <StyledAvatarWrapper $front>
        <Avatar user={otherUsers[0]} width={AVATAR_WIDTH} />
      </StyledAvatarWrapper>
    </>
  );
};

const getDateComponent = (date: Date) => <>{moment(date).format('llll').toString()}</>;

export function generateConvoItem({
  convo,
  me,
  showDate,
  orgName,
}: {
  convo: ConversationType;
  me: MessagingMe;
  showDate?: boolean;
  orgName?: string;
}) {
  const { subject, lastMessageCreatedAt, id, isUnread, lastMessageBody } =
    convo;
  const AvatarComponent = getAvatar(convo);
  const lastSenderName = getLastSenderUsername(convo, me);
  const DateComponent = showDate
    ? getDateComponent(convo.lastMessageCreatedAt)
    : null;
  return {
    onItemClick: () => {
      onConvoItemClick(id);
    },
    AvatarComponent,
    DateComponent,
    MainComponent: (
      <StyledGridCol>
        <Text strong>{getOtherName(convo)}</Text>
        <StyledGridCol>
          <Text strong>{subject}</Text>
          <Text>
            {lastSenderName}: {lastMessageBody}
          </Text>
        </StyledGridCol>
        {orgName && <Text color="red">{orgName}</Text>}
      </StyledGridCol>
    ),
    date: lastMessageCreatedAt,
    showBadge: isUnread,
  };
}

const getIsUnread = (
  convo: ConversationType,
  unreadConvos: ConversationType[],
) => convo && unreadConvos?.some(curConvo => curConvo.id === convo.id);

export const cleanConvo = (convo: ConversationType, me: MessagingMe) => {
  const userInfo = cloneDeep(convo.userInfo);
  // Add admin if it needs to be added
  if (convo.includeAdmins) {
    userInfo.unshift({
      id: 'FRONTDESK_ADMIN',
      firstName: 'Frontdesk',
      lastName: 'Admins',
    });
  }

  return {
    isUnread: getIsUnread(convo, me.unreadConvos),
    ...convo,
    userInfo,
    otherUsers: userInfo.filter(user => user.id !== me.id),
  };
};
